import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { UserPlus, Trash2, RotateCcw, AlertTriangle, Loader2, X } from 'lucide-react';
import { RatingBadge } from '../components/VisualAtoms';
import { ActionButton } from '../components/VisualAtoms';
import { toneForRating, TONE } from '../theme/adminVisualSystem';
import SectionAnalyticsHeader from '../components/SectionAnalyticsHeader';
import AddExternalArtistModal from '../components/AddExternalArtistModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function numericToRatingKey(n) {
  if (n == null) return null;
  if (typeof n === 'string') return n;
  if (n >= 4) return 'genius';
  if (n === 3) return 'very_good';
  if (n === 2) return 'good';
  return 'average';
}

// ─── Branded confirmation modal ────────────────────────────────
// Matches the curator-OS aesthetic — used for soft-delete + permanent
// delete prompts so we never fall back to ``window.confirm``.
function ConfirmModal({ open, title, body, confirmLabel, confirmTone, busy, onConfirm, onCancel }) {
  const tone = confirmTone || 'danger';
  const borderClass = tone === 'danger'
    ? 'border-[rgba(232,139,160,0.55)] text-[#e88ba0] hover:bg-[rgba(232,139,160,0.08)]'
    : 'border-[rgba(228,192,122,0.55)] text-[#e4c07a] hover:bg-[rgba(228,192,122,0.08)]';
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          onClick={onCancel}
          data-testid="artist-confirm-modal"
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <motion.div
            initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.97, opacity: 0 }}
            transition={{ duration: 0.18, ease: [0.2, 0.7, 0.2, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-[460px] bg-[#070707] border border-[#1a1a1a]"
          >
            <div className="p-5 flex items-start gap-3 border-b border-[#1a1a1a]">
              <div className="w-9 h-9 border border-[rgba(232,139,160,0.45)] bg-[rgba(232,139,160,0.06)] flex items-center justify-center shrink-0">
                <AlertTriangle className="w-4 h-4 text-[#e88ba0]" />
              </div>
              <div className="min-w-0">
                <h3 className="text-[13px] font-outfit text-white tracking-tight">{title}</h3>
                <p className="mt-1 text-[11px] font-mono text-[#888] leading-relaxed">{body}</p>
              </div>
            </div>
            <div className="px-5 py-3 flex items-center justify-end gap-2">
              <button
                onClick={onCancel} disabled={busy}
                data-testid="artist-confirm-cancel"
                className="text-[10px] font-mono uppercase tracking-[0.2em] px-4 py-2 border border-[#222] text-[#aaa] hover:text-white hover:border-[#444] disabled:opacity-40"
              >Cancel</button>
              <button
                onClick={onConfirm} disabled={busy}
                data-testid="artist-confirm-ok"
                className={`text-[10px] font-mono uppercase tracking-[0.2em] px-4 py-2 border inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed ${borderClass}`}
              >
                {busy && <Loader2 className="w-3 h-3 animate-spin" />}
                {confirmLabel}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function ArtistsPage() {
  const navigate = useNavigate();
  const { exhibitionId } = useOutletContext() || {};
  const [artists, setArtists] = useState([]);
  const [removed, setRemoved] = useState([]); // soft-deleted list (separate fetch)
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [ratingFilter, setRatingFilter] = useState('all');
  const [tab, setTab] = useState('active'); // 'active' | 'removed'
  // Confirm modal state — single store for soft-delete, restore (no
  // modal), and permanent delete prompts.
  const [confirm, setConfirm] = useState(null); // {action: 'soft'|'permanent', artist}
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const fetchArtists = () => {
    setLoading(true);
    Promise.allSettled([
      axios.get(`${API}/admin/artists`),
      axios.get(`${API}/admin/artists?deleted=true`),
    ])
      .then(([active, removedRes]) => {
        setArtists(active.status === 'fulfilled' ? active.value.data : []);
        setRemoved(removedRes.status === 'fulfilled' ? removedRes.value.data : []);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchArtists(); }, []);

  const visibleList = tab === 'active' ? artists : removed;

  const filtered = useMemo(() => {
    let out = visibleList;
    if (tab === 'active' && ratingFilter !== 'all') {
      out = out.filter((a) => {
        const k = numericToRatingKey(a.global_rating);
        if (ratingFilter === 'unrated') return !k;
        return k === ratingFilter;
      });
    }
    if (search) {
      const s = search.toLowerCase();
      out = out.filter(
        (a) =>
          (a.artist_name || '').toLowerCase().includes(s) ||
          (a.email || '').toLowerCase().includes(s),
      );
    }
    return out;
  }, [visibleList, search, ratingFilter, tab]);

  const totals = useMemo(() => {
    const t = { average: 0, good: 0, very_good: 0, genius: 0, unrated: 0 };
    for (const a of artists) {
      const k = numericToRatingKey(a.global_rating);
      if (!k) t.unrated += 1;
      else if (t[k] !== undefined) t[k] += 1;
    }
    return t;
  }, [artists]);

  // ─── Row actions ────────────────────────────────────────────
  const askSoftDelete = (artist, e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    setConfirm({ action: 'soft', artist });
  };
  const askPermanentDelete = (artist, e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    setConfirm({ action: 'permanent', artist });
  };
  const handleRestore = async (artist, e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    try {
      await axios.patch(`${API}/admin/artists/${artist.id}/restore`);
      toast.success(`${artist.artist_name || 'Artist'} restored`);
      fetchArtists();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Restore failed');
    }
  };
  // Toggle whether this artist appears in the homepage "Meet our Artists" strip.
  const toggleShowOnMain = async (artist, e) => {
    if (e) { e.stopPropagation(); e.preventDefault(); }
    const next = !(artist.show_on_main !== false);
    setTogglingId(artist.id);
    setArtists((prev) => prev.map((x) => (x.id === artist.id ? { ...x, show_on_main: next } : x)));
    try {
      await axios.patch(`${API}/admin/artists/${artist.id}`, { show_on_main: next });
      toast.success(
        next
          ? `${artist.artist_name || 'Artist'} will show on the main page`
          : `${artist.artist_name || 'Artist'} hidden from the main page`,
      );
    } catch (err) {
      setArtists((prev) => prev.map((x) => (x.id === artist.id ? { ...x, show_on_main: !next } : x)));
      toast.error(err?.response?.data?.detail || 'Update failed');
    } finally {
      setTogglingId(null);
    }
  };
  const performConfirm = async () => {
    if (!confirm) return;
    setConfirmBusy(true);
    try {
      if (confirm.action === 'soft') {
        await axios.patch(`${API}/admin/artists/${confirm.artist.id}/delete`);
        toast.success(`${confirm.artist.artist_name || 'Artist'} moved to Removed`);
      } else {
        await axios.delete(`${API}/admin/artists/${confirm.artist.id}/permanent`);
        toast.success(`${confirm.artist.artist_name || 'Artist'} permanently removed`);
      }
      setConfirm(null);
      fetchArtists();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Action failed');
    } finally {
      setConfirmBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <SectionAnalyticsHeader
        title="Artist Profiles"
        helper="Long-term curator-rated artists across all exhibitions"
        items={artists}
        chartLabel="Profile Inflow"
        days={30}
        activeStatKey={ratingFilter}
        onStatClick={setRatingFilter}
        stats={[
          { key: 'genius', label: 'Genius', value: totals.genius, tone: toneForRating('genius') },
          { key: 'very_good', label: 'Very Good', value: totals.very_good, tone: toneForRating('very_good') },
          { key: 'good', label: 'Good', value: totals.good, tone: toneForRating('good') },
          { key: 'average', label: 'Average', value: totals.average, tone: toneForRating('average') },
          { key: 'unrated', label: 'Unrated', value: totals.unrated, tone: TONE.muted },
        ]}
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setTab('active')}
            data-testid="artists-tab-active"
            className={`text-[10px] font-mono uppercase tracking-[0.2em] px-3 py-1.5 border transition-colors ${
              tab === 'active'
                ? 'border-[#90E0EF] text-[#90E0EF] bg-[rgba(144,224,239,0.06)]'
                : 'border-[#222] text-[#777] hover:text-white hover:border-[#444]'
            }`}
          >
            Active · {artists.length}
          </button>
          <button
            type="button"
            onClick={() => setTab('removed')}
            data-testid="artists-tab-removed"
            className={`text-[10px] font-mono uppercase tracking-[0.2em] px-3 py-1.5 border transition-colors ${
              tab === 'removed'
                ? 'border-[#e88ba0] text-[#e88ba0] bg-[rgba(232,139,160,0.06)]'
                : 'border-[#222] text-[#777] hover:text-white hover:border-[#444]'
            }`}
          >
            Removed · {removed.length}
          </button>
        </div>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search artists..."
          className="bg-transparent border border-[#222] text-white text-xs font-mono px-3 py-2 w-full sm:w-72 focus:outline-none focus:border-[#90E0EF]"
        />
        {tab === 'active' && (
          <ActionButton
            variant="primary"
            icon={UserPlus}
            onClick={() => setShowAdd(true)}
            data-testid="add-external-artist-btn"
          >
            Add External Artist
          </ActionButton>
        )}
      </div>

      {loading ? (
        <p className="text-[#444] font-mono text-sm">Loading...</p>
      ) : filtered.length === 0 ? (
        <div className="border border-[#1a1a1a] bg-[#0d0d0d] p-12 text-center">
          <p className="text-[#666] font-mono text-sm">
            {tab === 'active' ? 'No artist profiles yet' : 'No removed artists'}
          </p>
          <p className="text-[11px] font-mono text-[#444] mt-2">
            {tab === 'active'
              ? 'Artist profiles are created when applications are accepted'
              : 'Removed artists land here until restored or permanently deleted'}
          </p>
        </div>
      ) : (
        <div className="border border-[#1a1a1a] overflow-x-auto">
          <table className="w-full text-xs font-mono">
            <thead>
              <tr className="border-b border-[#1a1a1a] text-[#666] uppercase tracking-[0.15em]">
                <th className="text-left p-3">Name</th>
                <th className="text-left p-3">Email</th>
                <th className="text-left p-3">Location</th>
                <th className="text-left p-3">Rating</th>
                {tab === 'active' && <th className="text-left p-3">Main Page</th>}
                {tab === 'removed' && <th className="text-left p-3">Removed</th>}
                <th className="text-right p-3 w-32">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => (
                <motion.tr
                  key={a.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  data-clickable="true"
                  className="group border-b border-[#111] hover:bg-[#111] hover:border-[#90E0EF]/15 transition-colors"
                >
                  <td
                    className="p-3 text-[#f6f6f6] cursor-pointer"
                    onClick={() => navigate(`/admin/artists/${a.id}`)}
                  >{a.artist_name}</td>
                  <td
                    className="p-3 text-[#aaa] cursor-pointer"
                    onClick={() => navigate(`/admin/artists/${a.id}`)}
                  >{a.email}</td>
                  <td
                    className="p-3 text-[#aaa] cursor-pointer"
                    onClick={() => navigate(`/admin/artists/${a.id}`)}
                  >{a.location || '—'}</td>
                  <td
                    className="p-3 cursor-pointer"
                    onClick={() => navigate(`/admin/artists/${a.id}`)}
                  >
                    <RatingBadge rating={numericToRatingKey(a.global_rating)} />
                  </td>
                  {tab === 'active' && (
                    <td className="p-3" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={(e) => toggleShowOnMain(a, e)}
                        disabled={togglingId === a.id}
                        role="switch"
                        aria-checked={a.show_on_main !== false}
                        data-testid={`artist-show-main-${a.id}`}
                        title={a.show_on_main !== false ? 'Showing on main page — click to hide' : 'Hidden from main page — click to show'}
                        className="inline-flex items-center gap-2 disabled:opacity-50"
                      >
                        <span className={`relative inline-block w-9 h-5 border transition-colors ${a.show_on_main !== false ? 'bg-[rgba(144,224,239,0.18)] border-[#90E0EF]' : 'bg-transparent border-[#333]'}`}>
                          <span className={`absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 transition-all duration-200 ${a.show_on_main !== false ? 'left-[16px] bg-[#90E0EF]' : 'left-[2px] bg-[#555]'}`} />
                        </span>
                        <span className={`text-[10px] uppercase tracking-[0.15em] ${a.show_on_main !== false ? 'text-[#90E0EF]' : 'text-[#666]'}`}>
                          {a.show_on_main !== false ? 'On' : 'Off'}
                        </span>
                      </button>
                    </td>
                  )}
                  {tab === 'removed' && (
                    <td className="p-3 text-[#777]">
                      {a.deleted_at ? new Date(a.deleted_at).toLocaleDateString() : '—'}
                    </td>
                  )}
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-1">
                      {tab === 'active' ? (
                        <button
                          type="button"
                          onClick={(e) => askSoftDelete(a, e)}
                          data-testid={`artist-row-delete-${a.id}`}
                          className="opacity-60 group-hover:opacity-100 text-[#777] hover:text-[#e88ba0] p-1.5 border border-transparent hover:border-[rgba(232,139,160,0.4)] transition-all"
                          title="Move to Removed"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={(e) => handleRestore(a, e)}
                            data-testid={`artist-row-restore-${a.id}`}
                            className="opacity-80 group-hover:opacity-100 text-[#7ed4a0] hover:text-[#9ae0b5] p-1.5 border border-[rgba(126,212,160,0.4)] hover:bg-[rgba(126,212,160,0.06)] transition-all"
                            title="Restore to Active"
                          >
                            <RotateCcw className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => askPermanentDelete(a, e)}
                            data-testid={`artist-row-purge-${a.id}`}
                            className="opacity-80 group-hover:opacity-100 text-[#e88ba0] hover:text-[#f0a5b9] p-1.5 border border-[rgba(232,139,160,0.4)] hover:bg-[rgba(232,139,160,0.06)] transition-all"
                            title="Delete permanently"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AddExternalArtistModal
        open={showAdd}
        onClose={() => setShowAdd(false)}
        exhibitionId={exhibitionId}
        onCreated={() => fetchArtists()}
      />

      <ConfirmModal
        open={!!confirm}
        title={
          confirm?.action === 'permanent'
            ? 'Delete this artist permanently?'
            : 'Move this artist to Removed?'
        }
        body={
          confirm?.action === 'permanent'
            ? `“${confirm?.artist?.artist_name || 'Untitled'}” will be wiped from artist_profiles. Related open-call applications stay (with the link cleared); email logs are kept for audit. This cannot be undone.`
            : `“${confirm?.artist?.artist_name || 'Untitled'}” will move to the Removed tab. You can restore them or wipe them permanently from there. No emails, applications or other records are touched.`
        }
        confirmLabel={confirm?.action === 'permanent' ? 'Yes, delete permanently' : 'Yes, move to Removed'}
        busy={confirmBusy}
        onConfirm={performConfirm}
        onCancel={() => (confirmBusy ? null : setConfirm(null))}
      />
    </div>
  );
}
